import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Container, Box } from '@mui/material';

// Core components - loaded immediately (needed for initial render)
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/ScrollToTop';
import Login from './components/Login';
import PasswordResetAction from './components/PasswordResetAction';
import { useAuth } from './hooks/useAuth';
import LoadingSpinner from './components/LoadingSpinner';
import { getOptionalModuleRoutes } from './modules/registry/moduleRegistry';
import { isModuleEnabled, MODULE_IDS } from './config/moduleConfig';
import { DEPLOYMENT_CONFIG } from './config/deploymentConfig';

// Lazy-loaded components - loaded on demand (code splitting)
const LandingPage = lazy(() => import('./components/LandingPage'));
const TruckCheckForm = lazy(() => import('./components/TruckCheckForm'));
const TruckManagementMenu = lazy(() => import('./components/TruckManagementMenu'));
const TruckManagement = lazy(() => import('./components/TruckManagement'));
const TruckStatus = lazy(() => import('./components/TruckStatus'));
const KitDashboard = lazy(() => import('./components/KitDashboard'));
const KitManagement = lazy(() => import('./components/KitManagement'));
const KitInstanceManagement = lazy(() => import('./components/KitInstanceManagement'));
const KitCheckoff = lazy(() => import('./components/KitCheckoffNew'));
const KitStatusPage = lazy(() => import('./components/KitStatusPage'));
const InventoryLanding = lazy(() => import('./components/InventoryLanding'));
const InventoryManagement = lazy(() => import('./components/InventoryManagement'));
const SupplierManagement = lazy(() => import('./components/SupplierManagement'));
const PersonnelManagement = lazy(() => import('./components/PersonnelManagement'));
const ComingSoon = lazy(() => import('./components/ComingSoon'));
const ReleaseNotes = lazy(() => import('./components/ReleaseNotes'));
const FirestoreListenerTest = lazy(() => import('./components/debug/FirestoreListenerTest'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const TruckMaintenancePage = lazy(() => import('./components/TruckMaintenancePage'));

function App() {
  const location = useLocation();
  const { user, loading, userRole } = useAuth();
  const optionalModuleRoutes = getOptionalModuleRoutes();
  const inventoryEnabled = isModuleEnabled(MODULE_IDS.INVENTORY);
  const kitCheckoffEnabled = isModuleEnabled(MODULE_IDS.KIT_CHECKOFF);
  const kitsEnabled = isModuleEnabled(MODULE_IDS.KITS);
  const maintenanceEnabled = isModuleEnabled(MODULE_IDS.MAINTENANCE);
  const personnelEnabled = isModuleEnabled(MODULE_IDS.PERSONNEL);
  const { appName } = DEPLOYMENT_CONFIG.labels;
  const isAuthActionRoute = location.pathname === '/auth/action';

  if (isAuthActionRoute) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.default'
      }}>
        <Header />
        <Container
          component="main"
          maxWidth="sm"
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4
          }}
        >
          <PasswordResetAction />
        </Container>
        <Footer />
      </Box>
    );
  }

  // Show loading spinner while checking authentication
  if (loading) {
    return <LoadingSpinner message={`Loading ${appName}...`} />;
  }

  // If user is not authenticated, show login page
  if (!user) {
    process.env.REACT_APP_ENVIRONMENT === 'development' && console.log('🔒 No user, showing login page');
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        backgroundColor: 'background.default' 
      }}>
        <Header />
        <Container 
          component="main" 
          maxWidth="sm" 
          sx={{ 
            flex: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 4 
          }}
        >
          <Login />
        </Container>
        <Footer />
      </Box>
    );
  }

  // If user doesn't have permission to view forms, show access denied
  if (userRole === 'DENIED') {
    process.env.REACT_APP_ENVIRONMENT === 'development' && console.log('❌ User access denied, showing denied page');
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        backgroundColor: 'background.default' 
      }}>
        <Header user={user} userRole={userRole} />
        <Container 
          component="main" 
          maxWidth="md" 
          sx={{ 
            flex: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 4 
          }}
        >
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <h1>🔒 Access Denied</h1>
            <p>You do not have permission to access {appName}.</p>
            <p>Contact your administrator to be granted access.</p>
          </Box>
        </Container>
        <Footer />
      </Box>
    );
  }

  // Main authenticated app
  process.env.REACT_APP_ENVIRONMENT === 'development' && console.log('✅ User authenticated, showing main app');
  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: 'background.default' 
    }}>
      <Header user={user} userRole={userRole} />
      <ScrollToTop />
      
      <Box
        component="main"
        sx={{
          flex: 1,
          py: 3,
          px: { xs: 2, sm: 3 } // Responsive padding
        }}
      >
        <Suspense fallback={<LoadingSpinner message="Loading..." />}>
          <Routes>
          {/* Main landing page */}
          <Route
            path="/"
            element={<LandingPage user={user} userRole={userRole} />}
          />
          
          {/* Truck Management Menu */}
          <Route 
            path="/truck-management" 
            element={<TruckManagementMenu user={user} userRole={userRole} />} 
          />
          
          {/* Truck Maintenance */}
          {maintenanceEnabled && (
            <Route
              path="/truck-management/:truckId/maintenance"
              element={<TruckMaintenancePage />}
            />
          )}

          {/* Truck check form */}
          <Route
            path="/truck-check"
            element={<TruckCheckForm user={user} userRole={userRole} />}
          />
          
          {/* Truck status (Regular users) */}
          <Route 
            path="/truck-status" 
            element={<TruckStatus />} 
          />
          
          {/* Coming soon page */}
          <Route 
            path="/coming-soon" 
            element={<ComingSoon />} 
          />
          
          {/* Kit Dashboard (Admin only) */}
          {kitsEnabled && (
            <Route
              path="/kit-management"
              element={<KitDashboard />}
            />
          )}
          
          {/* Kit Templates (Admin only) */}
          {kitsEnabled && (
            <Route
              path="/kit-templates"
              element={<KitManagement />}
            />
          )}
          
          {/* Kit Instance Management (Admin only) */}
          {kitsEnabled && (
            <Route
              path="/kit-instances"
              element={<KitInstanceManagement />}
            />
          )}
          
          {/* Kit Checkoff (Admin/Inspector) */}
          {kitCheckoffEnabled && (
            <Route
              path="/kit-checkoff"
              element={<KitCheckoff />}
            />
          )}

          {/* Kit Status Page */}
          {kitsEnabled && (
            <Route
              path="/kit-status/:kitInstanceId"
              element={<KitStatusPage />}
            />
          )}

          {/* Kit Checkoff with Instance ID */}
          {kitCheckoffEnabled && (
            <Route
              path="/kit-checkoff/:kitInstanceId"
              element={<KitCheckoff />}
            />
          )}
          
          {/* Truck Administration (Admin only) */}
          <Route 
            path="/truck-admin" 
            element={<TruckManagement />} 
          />
          
          {/* Release Notes */}
          <Route 
            path="/release-notes" 
            element={<ReleaseNotes />} 
          />
          
          {/* Debug Tools (Admin only) */}
          <Route 
            path="/debug/firestore-listeners" 
            element={<FirestoreListenerTest />} 
          />
          
          {/* Inventory Landing (Admin only) */}
          {inventoryEnabled && (
            <Route
              path="/inventory"
              element={<InventoryLanding />}
            />
          )}

          {/* Supply Management (Admin only) */}
          {inventoryEnabled && (
            <Route
              path="/inventory/supplies"
              element={<InventoryManagement />}
            />
          )}

          {/* Supplier Management (Admin only) */}
          {inventoryEnabled && (
            <Route
              path="/inventory/suppliers"
              element={<SupplierManagement />}
            />
          )}

          {personnelEnabled && (
            <Route
              path="/personnel"
              element={<PersonnelManagement />}
            />
          )}

          {optionalModuleRoutes.map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}
          
          {/* Admin Dashboard (Admin only) */}
          <Route
            path="/admin"
            element={
              user && userRole === 'ADMIN' ? (
                <AdminDashboard />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          
          {/* Redirect any unknown routes to main landing page */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Box>
      
      <Footer />
    </Box>
  );
}

export default App;
